<template>
  <div>
    <!-- 空状态 -->
    <div v-if="!hasData" class="emp">
      <div class="es">[ 暂无数据 ]</div>
      <h3>等待诊断数据</h3>
      <p>请先在<a href="javascript:void(0)" @click="$emit('switch-tab','ai')">「AI诊断」</a>或<a href="javascript:void(0)" @click="$emit('switch-tab','scores')">「评分详情」</a>中完成诊断。</p>
    </div>

    <!-- 报告内容 -->
    <div v-else>
      <!-- AI 横幅 -->
      <div v-if="aiMode" class="ai-banner">
        <div class="ab-icon">AI</div>
        <div class="ab-text">
          <strong>AI自动诊断</strong>
          <span class="sep">|</span> 品牌：<strong>{{ aiBrand }}</strong>
          <span class="sep">|</span> 行业：<strong>{{ aiIndustry }}</strong>
          <span class="sep">|</span> 18项指标已自动评分（可在「评分详情」中逐项调整）
        </div>
      </div>

      <!-- 四格统计 -->
      <div class="stat-grid">
        <div class="stat">
          <div class="sv" :class="gradeInfo.code">{{ totalScore }}</div>
          <div class="sl">健康指数</div>
        </div>
        <div class="stat">
          <div class="sv" :class="gradeInfo.code">{{ gradeInfo.grade }}</div>
          <div class="sl">诊断结论</div>
        </div>
        <div class="stat">
          <div class="sv cb">{{ healthyCount }}<span style="font-size:0.9rem;">/6</span></div>
          <div class="sl">结构健全</div>
        </div>
        <div class="stat">
          <div class="sv cr">{{ weakCount }}<span style="font-size:0.9rem;">/6</span></div>
          <div class="sl">结构薄弱</div>
        </div>
      </div>

      <!-- 综合诊断 -->
      <div class="diagnosis-panel">
        <div class="panel-header">
          <span class="pdot cyan"></span>综合诊断
        </div>
        <div class="panel-body">
          <p style="font-size:0.91rem;">{{ gradeInfo.detail }}</p>
          <p style="font-size:0.71rem;color:var(--text3);margin-top:8px;">
            <template v-if="aiMode">
              本报告由AI自动生成，标注"AI评分"的为高可靠搜索数据，"AI推断"为基于搜索结果的推理，"默认值"建议手动确认。
            </template>
            <template v-else>
              {{ brmConfirmCount }}题已由用户确认评分。
            </template>
          </p>
        </div>
      </div>

      <!-- 六维得分看板 -->
      <div class="diagnosis-panel">
        <div class="panel-header">
          <span class="pdot cyan"></span>六维得分看板
        </div>
        <div class="panel-body">
          <div class="gg">
            <div
              v-for="ds in dimensionScores"
              :key="ds.id"
              class="gauge"
            >
              <div class="gt">
                <span class="gn">{{ ds.name }}</span>
                <span class="gv" :style="{ color: ds.color }">{{ ds.score }}</span>
              </div>
              <div class="gtr">
                <div
                  class="gf"
                  :style="{ width: ds.score + '%', background: ds.color, boxShadow: '0 0 8px ' + ds.color }"
                ></div>
              </div>
              <div class="gi">理论：{{ ds.theory }}</div>
              <div class="gi">
                细项：
                <template v-for="(d, i) in ds.details">
                  {{ d.score }}/5
                  <span
                    v-if="aiMode"
                    :style="{ color: confidenceColor(d.confidence) }"
                  >●</span>
                  <template v-if="i < ds.details.length - 1"> · </template>
                </template>
                &ensp;｜ 权重：{{ ds.weight }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 诊断详情 -->
      <div class="diagnosis-panel">
        <div class="panel-header">
          <span class="pdot cyan"></span>诊断详情
          <span v-if="aiMode" class="pid">● 绿=AI高可靠 ● 黄=AI推断 ● 灰=默认值</span>
        </div>
        <div class="panel-body">
          <p style="color:var(--text2);font-size:0.8rem;margin-bottom:14px;">
            低分不是数字——每一项背后都是一个正在失血的伤口。
          </p>

          <div class="g2">
            <!-- 结构健全 -->
            <div>
              <h4 style="font-size:0.81rem;margin-bottom:10px;color:#00F5A0;font-family:var(--mono);">结构健全（≥70分）</h4>
              <template v-if="healthyDims.length">
                <div v-for="d in healthyDims" :key="d.id" class="st stg">
                  <span class="sd"></span>{{ d.name }} <strong>{{ d.score }}分</strong>
                </div>
              </template>
              <div v-else style="color:var(--text3);font-size:0.77rem;">暂无维度达标</div>
            </div>

            <!-- 结构预警 -->
            <div>
              <h4 style="font-size:0.81rem;margin-bottom:10px;color:#FFB800;font-family:var(--mono);">结构预警（50-69分）</h4>
              <template v-if="warningDims.length">
                <div
                  v-for="d in warningDims"
                  :key="d.id"
                  style="padding:10px 14px;background:var(--amber-dim);border:1px solid rgba(255,184,0,0.15);margin-bottom:6px;font-size:0.8rem;border-left:3px solid #FFB800;"
                >
                  <div style="font-family:var(--mono);">{{ d.name }} <strong style="color:#FFB800;">{{ d.score }}分</strong></div>
                  <div style="font-size:0.71rem;color:var(--text2);margin-top:2px;">{{ d.harmMessage }}</div>
                </div>
              </template>
              <div v-else style="color:var(--text3);font-size:0.77rem;">所有需关注维度已改善</div>
            </div>
          </div>

          <!-- 结构薄弱 -->
          <div style="margin-top:12px;">
            <h4 style="font-size:0.81rem;margin-bottom:10px;color:#FF3B5C;font-family:var(--mono);">结构薄弱（&lt;50分）</h4>
            <template v-if="weakDims.length">
              <div class="g2" style="gap:8px;">
                <div
                  v-for="d in weakDims"
                  :key="d.id"
                  style="padding:12px 14px;background:var(--red-dim);border:1px solid rgba(255,59,92,0.15);font-size:0.81rem;border-left:3px solid #FF3B5C;"
                >
                  <div style="font-weight:600;color:#FF3B5C;font-family:var(--mono);margin-bottom:4px;">
                    {{ d.name }} {{ d.score }}分
                  </div>
                  <div style="font-size:0.73rem;color:var(--text2);line-height:1.6;">{{ d.harmMessage }}</div>
                </div>
              </div>
            </template>
            <div v-else style="color:var(--text3);font-size:0.77rem;">所有维度均在安全线以上</div>
          </div>
        </div>
      </div>

      <!-- 跳转按钮 -->
      <div style="text-align:center;margin-top:14px;">
        <button class="btn btn-pri" style="padding:12px 30px;" @click="$emit('switch-tab','recommendations')">
          查看优化建议 →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'ReportTab',
  computed: {
    ...mapState('diagnosis', ['aiMode', 'aiBrand', 'aiIndustry', 'scores', 'brmSource']),
    ...mapGetters('diagnosis', ['dimensionScores', 'totalScore', 'gradeInfo']),

    hasData() {
      return this.scores && Object.keys(this.scores).length > 0
    },

    healthyCount() {
      return this.dimensionScores.filter(d => d.score >= 85).length
    },

    weakCount() {
      return this.dimensionScores.filter(d => d.score < 50).length
    },

    brmConfirmCount() {
      return Object.keys(this.brmSource).length
    },

    healthyDims() {
      return this.dimensionScores.filter(d => d.score >= 70)
    },

    warningDims() {
      return this.dimensionScores.filter(d => d.score >= 50 && d.score < 70)
    },

    weakDims() {
      return this.dimensionScores.filter(d => d.score < 50)
    }
  },
  methods: {
    confidenceColor(conf) {
      return conf === 'high' ? 'var(--green)' : conf === 'mid' ? 'var(--amber)' : 'var(--text3)'
    }
  }
}
</script>
